import React from 'react'
import intl from 'react-intl-universal'

import { ComposerSettings } from '~/types'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const styles = require('./TansaSettings.styles.scss')

type TansaSettingsData = ComposerSettings['proofreaders']['tansa'] & {
  websiteUserIds: {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    [website: symbol]: string
  }
}

interface Props extends TansaSettingsData {
  websites: Array<{
    _id: string
    display_name: string
  }>
  onChange: (tansaSettings: TansaSettingsData) => void
}

/*
  TypeScript has open bugs on allowing symbols as object indexes.
  https://github.com/microsoft/TypeScript/issues/1863

  This is valid JS so we are ignoring type errors for now.
*/
export const blankEntryKey = Symbol('blank tansa entry')

function makeWebsiteMap(entries: [string | symbol, string][]) {
  return entries.reduce((result, [websiteId, userId]) => {
    return {
      ...result,
      [websiteId]: userId,
    }
  }, {})
}

export default function TansaSettings(props: Props) {
  const blankEntries: [symbol, string][] = Object.getOwnPropertySymbols(
    props.websiteUserIds
  ).map((symbol) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    return [symbol, props.websiteUserIds[symbol]]
  })
  const userEntries = [...Object.entries(props.websiteUserIds), ...blankEntries]

  /*
    <input /> elements currently use defaultValue + onBlur to prevent
    the entire Angular settings module from re-rendering on each keystroke.

    This can be changed to the more standard value + onChange once settings
    is rewritten in React.
  */
  return (
    <div className={styles.root}>
      <div className="mb-3">
        <label htmlFor="tansa-company-id">
          {intl.get('settings.tansa.companyId')}
        </label>
        <input
          type="text"
          className="form-control"
          id="tansa-company-id"
          defaultValue={props.companyId}
          onBlur={(event) => {
            props.onChange({
              companyId: event.target.value,
              clientUrl: props.clientUrl,
              globalUserId: props.globalUserId,
              websiteUserIds: props.websiteUserIds,
            })
          }}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="tansa-global-user-id">
          {intl.get('settings.tansa.globalUserId')}
        </label>
        <input
          type="text"
          className="form-control"
          id="tansa-global-user-id"
          defaultValue={props.globalUserId}
          onBlur={(event) => {
            props.onChange({
              globalUserId: event.target.value,
              clientUrl: props.clientUrl,
              companyId: props.companyId,
              websiteUserIds: props.websiteUserIds,
            })
          }}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="tansa-client-url">
          {intl.get('settings.tansa.clientUrl')}
        </label>
        <input
          type="text"
          className="form-control"
          id="tansa-client-url"
          defaultValue={props.clientUrl}
          onBlur={(event) => {
            props.onChange({
              clientUrl: event.target.value,
              globalUserId: props.globalUserId,
              companyId: props.companyId,
              websiteUserIds: props.websiteUserIds,
            })
          }}
        />
      </div>

      <div className="mb-3">
        <div className="mb-1 bold">
          {intl.get('settings.tansa.dictionaryTitle')}
        </div>

        <div className="user-ids-grid">
          <label className="mb-0">
            {intl.get('settings.tansa.websiteLabel')}
          </label>
          <label className="mb-0">{intl.get('settings.tansa.userId')}</label>
          <span />

          {userEntries.map(([websiteId, userId], index) => {
            const key =
              typeof websiteId === 'symbol' ? websiteId.toString() : websiteId

            return (
              <React.Fragment key={key}>
                {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                <select
                  className="form-control"
                  value={typeof websiteId === 'symbol' ? '' : websiteId}
                  onChange={(event) => {
                    const selectedWebsite = event.target.value

                    const updatedEntries = [...userEntries]
                    updatedEntries.splice(index, 1, [selectedWebsite, userId])

                    props.onChange({
                      websiteUserIds: makeWebsiteMap(updatedEntries),
                      clientUrl: props.clientUrl,
                      companyId: props.companyId,
                      globalUserId: props.globalUserId,
                    })
                  }}
                >
                  <option value="" disabled>
                    --
                  </option>
                  {props.websites.map((website) => (
                    <option
                      key={website._id}
                      value={website._id}
                      disabled={props.websiteUserIds[website._id] !== undefined}
                    >
                      {website.display_name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control"
                  defaultValue={userId}
                  onBlur={(event) => {
                    props.onChange({
                      websiteUserIds: {
                        ...props.websiteUserIds,
                        [websiteId]: event.target.value,
                      },
                      clientUrl: props.clientUrl,
                      companyId: props.companyId,
                      globalUserId: props.globalUserId,
                    })
                  }}
                />
                <button
                  data-testid={`btn-delete-tansa_${key}`}
                  onClick={() => {
                    const updatedEntries = [...userEntries]
                    updatedEntries.splice(index, 1)

                    props.onChange({
                      websiteUserIds: makeWebsiteMap(updatedEntries),
                      clientUrl: props.clientUrl,
                      companyId: props.companyId,
                      globalUserId: props.globalUserId,
                    })
                  }}
                >
                  <i className="fa fa-trash-o c-brand-danger" />
                </button>
              </React.Fragment>
            )
          })}
        </div>
      </div>

      <button
        className="btn btn-md btn-secondary"
        // See note on TS bug with Symbols
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        disabled={props.websiteUserIds[blankEntryKey] !== undefined}
        onClick={() => {
          props.onChange({
            websiteUserIds: {
              ...props.websiteUserIds,
              [blankEntryKey]: '',
            },
            clientUrl: props.clientUrl,
            companyId: props.companyId,
            globalUserId: props.globalUserId,
          })
        }}
      >
        {intl.get('settings.tansa.addButton')}
      </button>
    </div>
  )
}
